import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { logDevBackendHealthOnce } from './utils/devBackendHealth';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';

// App version - increment on deploy to detect stale clients
const APP_VERSION = '1.1.11';
const SW_VERSION_EXPECTED = '3.2.0';
if (typeof window !== 'undefined') {
  window.__ARTMO_APP_VERSION = APP_VERSION;
  window.__ARTMO_SW_UPDATE_AVAILABLE = false;
}
const VERSION_KEY = 'artmosphere-app-version';
const RELOAD_FLAG_KEY = 'artmosphere-reload-in-progress';
const RELOAD_TIMESTAMP_KEY = 'artmosphere-reload-timestamp';

// DISABLED AUTO-RELOAD - Only update version, don't force reload
// This prevents infinite refresh loops
const storedVersion = localStorage.getItem(VERSION_KEY);
if (storedVersion !== APP_VERSION) {
  console.log(`[INDEX] Version updated: ${storedVersion} -> ${APP_VERSION}`);
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  // Clear any old reload flags
  sessionStorage.removeItem(RELOAD_FLAG_KEY);
  sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
} else if (!storedVersion) {
  localStorage.setItem(VERSION_KEY, APP_VERSION);
}

// Clear any existing reload flags on page load
sessionStorage.removeItem(RELOAD_FLAG_KEY);
sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);

const isStaging = (typeof window !== 'undefined' && (window.location.hostname || '').includes('staging')) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV === 'staging');
if (typeof window !== 'undefined' && isStaging) {
    const pageControlledBySw = !!(navigator.serviceWorker && navigator.serviceWorker.controller);
    console.log('[staging] bootstrap', {
      currentBundleVersion: APP_VERSION,
      swVersionExpected: SW_VERSION_EXPECTED,
      pageControlledBySw,
      origin: window.location.origin,
      ua: navigator.userAgent ? (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') ? 'Safari' : 'other') : 'unknown',
    });
    if (pageControlledBySw) {
      console.log('[staging] Page is controlled by a service worker. After deploy, refresh once to load the new bundle.');
    }
    if (String(process.env.REACT_APP_DISABLE_SW_IN_STAGING || '').toLowerCase() === 'true') {
      console.log('[staging] Service worker registration disabled (REACT_APP_DISABLE_SW_IN_STAGING=true).');
    }
    // One-time dev/staging utility: force unregister stale service workers during testing
    window.__ARTMO_UNREGISTER_SW = function () {
      if (!('serviceWorker' in navigator)) return Promise.resolve();
      return navigator.serviceWorker.getRegistrations().then(function (regs) {
        regs.forEach(function (r) { r.unregister(); });
        console.log('[staging] Service workers unregistered. Reload to run without SW.');
      });
    };
}

// Global error handler to catch TDZ errors before React ErrorBoundary
window.addEventListener('error', (event) => {
  const error = event.error || event;
  const errorMessage = error?.message || error?.toString() || '';
  
  if (errorMessage.includes('Cannot access uninitialized variable') || 
      errorMessage.includes('before initialization') ||
      errorMessage.includes('TDZ')) {
    
    // Extract variable name from error message
    const variableMatch = errorMessage.match(/variable ['"]([^'"]+)['"]/i) || 
                         errorMessage.match(/['"]([a-zA-Z_$][a-zA-Z0-9_$]*)['"]/i) ||
                         errorMessage.match(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/i);
    
    const variableName = variableMatch ? variableMatch[1] : 'UNKNOWN';
    
    console.error('🚨 GLOBAL TDZ ERROR DETECTED:', {
      variable: variableName,
      errorMessage: errorMessage,
      stack: error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
    
    // Store in window for ErrorBoundary to access
    window.lastTDZError = {
      variable: variableName,
      error: errorMessage,
      stack: error?.stack,
      timestamp: new Date().toISOString()
    };
  }
}, true); // Use capture phase to catch errors early

// Initialize app
function initApp() {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('[INDEX] Root element not found!');
    document.body.innerHTML = '<div style="padding: 20px; text-align: center;"><h1>Error: Root element not found</h1><p>Please refresh the page.</p></div>';
    return;
  }

  const root = ReactDOM.createRoot(rootElement);
  if (isStaging) console.log('[staging] app render', { version: APP_VERSION });

  // Make validation functions available in browser console (lazy load to avoid initialization issues)
  if (typeof window !== 'undefined') {
    // Lazy load validation helpers to avoid initialization errors
    window.validateOptimizations = () => {
      import('./utils/validationHelper').then(module => {
        module.validateOptimizations();
      }).catch(err => {
        console.error('Failed to load validation helper:', err);
      });
    };
    window.quickValidate = () => {
      import('./utils/validationHelper').then(module => {
        module.quickValidate();
      }).catch(err => {
        console.error('Failed to load validation helper:', err);
      });
    };
    console.log('[INDEX] Validation helpers available: window.validateOptimizations(), window.quickValidate()');
  }

  try {
root.render(
      <ErrorBoundary>
  <BrowserRouter future={{ v7_relativeSplatPath: true }}>
    <App />
    <PWAInstallPrompt />
  </BrowserRouter>
      </ErrorBoundary>
    );
    logDevBackendHealthOnce();
  } catch (error) {
    console.error('[INDEX] Error rendering app:', error);
    rootElement.innerHTML = `
      <div style="padding: 20px; text-align: center; font-family: system-ui;">
        <h1 style="color: #d32f2f;">⚠️ Error Loading App</h1>
        <p>Please refresh the page. If the problem persists, clear your browser cache.</p>
        <p style="font-size: 12px; color: #666; margin-top: 10px;">Error: ${error.message}</p>
        <button onclick="window.location.reload()" style="margin-top: 20px; padding: 12px 24px; background: #FFC107; color: white; border: none; border-radius: 8px; font-size: 16px; cursor: pointer;">
          Reload Page
        </button>
      </div>
    `;
  }
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  // DOM is already ready
  initApp();
}

// Register service worker for PWA only in production-like environments.
// In local development, stale SW registrations can cause script load failures.
const disableSwInStaging = isStaging && String(process.env.REACT_APP_DISABLE_SW_IN_STAGING || '').toLowerCase() === 'true';
const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname || '');
const shouldRegisterServiceWorker =
  process.env.NODE_ENV === 'production' &&
  'serviceWorker' in navigator &&
  !disableSwInStaging &&
  !isLocalhost;

if ('serviceWorker' in navigator && (process.env.NODE_ENV !== 'production' || isLocalhost)) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => {
      if (!regs.length) return;
      const hadController = !!navigator.serviceWorker.controller;
      return Promise.all(regs.map((r) => r.unregister())).then(() => {
        // On localhost, force one clean reload after SW purge so stale cached preview/image responses
        // cannot survive in a previously controlled page.
        if (!isLocalhost || !hadController) return;
        const key = 'artmo_sw_purged_reload_once';
        if (sessionStorage.getItem(key) === '1') return;
        sessionStorage.setItem(key, '1');
        window.location.reload();
      });
    })
    .catch(() => {});
}

if (shouldRegisterServiceWorker) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`/service-worker.js?v=${APP_VERSION}&t=${Date.now()}`, {
        updateViaCache: 'none' // Always check for updates
      })
      .then((registration) => {
        console.log('[SW] Service Worker registered successfully:', registration.scope);

        // Safe update: avoid "newestWorker is null" when registration is in inconsistent state
        const safeUpdate = () => {
          try {
            if (!registration) {
              return;
            }
            
            // Check if registration is in a valid state
            if (typeof registration.update !== 'function') {
              return;
            }
            
            // Additional check: ensure registration has valid properties
            if (!registration.scope) {
              return;
            }
            
            // Call update with error handling
            try {
              registration.update();
            } catch (updateError) {
              // Some browsers throw (e.g. "newestWorker is null") when SW state is inconsistent
              // This is harmless - just skip the update check
              if (process.env.NODE_ENV === 'development') {
                console.warn('[SW] Update check skipped:', updateError?.message || updateError);
              }
            }
          } catch (e) {
            // Outer catch for any other errors
            if (process.env.NODE_ENV === 'development') {
              console.warn('[SW] Update check failed:', e?.message || e);
            }
          }
        };

        // Check for updates every 5 minutes (less frequent to reduce reload loops)
        setInterval(safeUpdate, 300000); // 5 minutes

        // Check for updates when app becomes visible (mobile-friendly) - but less frequently
        let lastVisibilityUpdate = 0;
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) {
            const now = Date.now();
            if (now - lastVisibilityUpdate > 30000) {
              lastVisibilityUpdate = now;
              safeUpdate();
            }
          }
        });

        // Check for updates when app comes to foreground (mobile) - but less frequently
        let lastFocusUpdate = 0;
        window.addEventListener('focus', () => {
          const now = Date.now();
          if (now - lastFocusUpdate > 30000) {
            lastFocusUpdate = now;
            safeUpdate();
          }
        });
        
        // Listen for service worker updates - set flag for "new version available", no auto-reload
        registration.addEventListener('updatefound', () => {
          try {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                try {
                    if (newWorker.state === 'installed') {
                    if (navigator.serviceWorker.controller) {
                      if (typeof window !== 'undefined') window.__ARTMO_SW_UPDATE_AVAILABLE = true;
                      const isStagingSw = typeof window !== 'undefined' && (window.location.hostname || '').includes('staging');
                      if (isStagingSw) {
                        console.log('[staging] New bundle deployed. Refresh this page to load it.');
                      } else {
                        console.log('[SW] New version available. Refresh to load.');
                      }
                      try {
                        newWorker.postMessage({ type: 'SKIP_WAITING' });
                      } catch (msgError) {
                        if (process.env.NODE_ENV === 'development') {
                          console.warn('[SW] Could not send SKIP_WAITING:', msgError?.message);
                        }
                      }
                    } else {
                      console.log('[SW] Service worker installed for the first time.');
                    }
                  }
                } catch (stateError) {
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('[SW] State change error:', stateError?.message);
                  }
                }
              });
            }
          } catch (updateError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[SW] Update found error:', updateError?.message);
            }
          }
        });
      })
      .catch((error) => {
        console.error('[SW] Service Worker registration failed:', error);
        // Don't block app if service worker fails
      });
  });
}

reportWebVitals();
